import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSwipeable } from 'react-swipeable'
import { IProductImage } from '../types'
import DynamicHeadphones from './customColours/DynamicHeadphones'
import { ICustomComponent, IProduct } from '@/context/ProductDataStore'

type ImageSlideshowProps = {
  images: IProductImage[]
  className?: string
  dynamicImage: IProductImage
  autoJumpToFirst?: boolean
  showEditColors?: boolean
  onEditColors?: () => void
  onAllImagesLoaded?: () => void
  customHero?: {
    backgroundUrl?: string
    customColourProduct: IProduct
    customComponents: ICustomComponent[]
  }
}

const ImageSlideshow: React.FC<ImageSlideshowProps> = ({
  images,
  className,
  dynamicImage,
  autoJumpToFirst = false,
  showEditColors = false,
  onEditColors,
  onAllImagesLoaded,
  customHero,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const loadedImagesRef = useRef<Set<string>>(new Set())
  const hasNotifiedLoadedRef = useRef(false)
  const onAllImagesLoadedRef = useRef(onAllImagesLoaded)

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onAllImagesLoadedRef.current = onAllImagesLoaded
  }, [onAllImagesLoaded])

  // Use useMemo to prevent unnecessary re-creation of allImages array
  const allImages = useMemo(() => [dynamicImage, ...images], [dynamicImage, images])

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipe('left'),
    onSwipedRight: () => handleSwipe('right'),
    preventScrollOnSwipe: true,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (autoJumpToFirst) {
        setCurrentIndex(0)
      }
    }, 8000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  // Create a stable reference for the images URLs to prevent infinite loops
  const imagesKey = useMemo(() => allImages.map(img => img.url).join(','), [allImages]);

  useEffect(() => {
    if (allImages.length === 0) return; // Avoid running if there are no images

    // Skip if we've already processed these exact images
    if (loadedImagesRef.current.has(imagesKey)) {
      return;
    }

    console.log('[imageLoadCheck] Starting to load images');
    loadedImagesRef.current.add(imagesKey);
    hasNotifiedLoadedRef.current = false;

    // Keep track of loaded URLs to avoid duplicates
    const loadedUrls = new Set<string>();

    // Clean up any existing preload links to avoid duplicates
    const existingLinks = document.head.querySelectorAll('link[rel="preload"][as="image"]');
    existingLinks.forEach(link => {
      if (allImages.some(img => img.url === (link as HTMLLinkElement).href)) {
        loadedUrls.add((link as HTMLLinkElement).href);
      }
    });

    // Only preload images that haven't been preloaded yet
    allImages.forEach(image => {
      if (!loadedUrls.has(image.url)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = image.url;
        link.onload = () => {
          console.log(`[imageLoadCheck] Preloaded image: ${image.url}`);
        };
        document.head.appendChild(link);
        loadedUrls.add(image.url);
      }
    });

    const imageLoadPromises = allImages.map(image => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.src = image.url;

        // If image is already cached, resolve immediately
        if (img.complete && img.naturalHeight !== 0) {
          resolve();
        } else {
          img.onload = () => {
            console.log(`[imageLoadCheck] Image loaded: ${image.url}`);
            resolve();
          };
          img.onerror = () => {
            console.warn(`[imageLoadCheck] Failed to load image: ${image.url}`);
            resolve(); // Still resolve to not block other images
          };
        }
      });
    });

    Promise.all(imageLoadPromises).then(() => {
      console.log('[imageLoadCheck] All images loaded');
      if (onAllImagesLoadedRef.current && !hasNotifiedLoadedRef.current) {
        hasNotifiedLoadedRef.current = true;
        onAllImagesLoadedRef.current();
      }
    });
  }, [imagesKey, allImages]); // Removed onAllImagesLoaded from dependencies

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index)
  }

  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % allImages.length)
    } else if (direction === 'right') {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + allImages.length) % allImages.length
      )
    }
  }

  return (
    <div className={`image-slideshow product-container flex flex-col w-full md:sticky top-[172px] self-start ${className} aspect-[6/5] md:aspect-[4/4.25] max-h-[calc(100vh-320px)] md:max-h-[calc(100vh-220px)]`}>

      <div
        {...swipeHandlers}
        className="product-image h-full bg-white flex justify-center items-center rounded-xl overflow-hidden aspect-[6/5] md:aspect-[4/4.25] relative">
        <div
          className="w-full h-full transition-transform duration-500 ease-kibushopdefault"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            display: 'flex',
          }}>
          {allImages.map((image, index) => {
            const shouldRenderCustomHero = Boolean(
              customHero &&
              index === 0 &&
              customHero.customComponents.length > 0
            )
            const heroBackground = customHero?.backgroundUrl ?? image.backgroundUrl

            return (
              <div key={index} className="w-full h-full flex-shrink-0 relative">
                {heroBackground && (
                  <img
                    src={heroBackground}
                    alt={`${image.alt} background`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {shouldRenderCustomHero ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-full relative scale-[0.95]">
                      <DynamicHeadphones
                        viewName="front_view"
                        engraving=""
                        customColourProduct={customHero!.customColourProduct}
                        customComponents={customHero!.customComponents}
                        toggleColourPicker={() => { }}
                        interactive={false}
                        className="transform-none"
                      />
                    </div>
                  </div>
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className={`absolute inset-0 w-full h-full transition-[filter] duration-300 ${index === 0 ? 'object-contain' : 'object-cover'} ${image.invertForeground ? 'invert' : ''}`}
                  />
                )}
              </div>
            )
          })}
        </div>
        {allImages.length > 1 && (
          <div className="md:hidden absolute bottom-3 left-0 right-0 flex flex-row justify-center items-center gap-2">
            {allImages.map((_, index) => (
              <button
                key={index}
                onClick={() => handleThumbnailClick(index)}
                className={`rounded-full transition-all duration-200 ${index === currentIndex ? 'bg-white w-2.5 h-2.5' : 'bg-white/50 w-2 h-2'}`}
              />
            ))}
          </div>
        )}
        {showEditColors && currentIndex === 0 && (
          <button
            onClick={onEditColors}
            className="absolute bottom-3 left-3 font-headline flex items-center gap-1 bg-white rounded-lg py-2 px-3 animate-gradient-text"
          >
            <span className="text-gradient">Edit Colours</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
              <path d="M2 2l7.586 7.586"></path>
              <circle cx="11" cy="11" r="2"></circle>
            </svg>
          </button>
        )}
      </div>


      {allImages.filter(image => image.showThumbnail).length > 1 && (
        <div className="product-thumbnails hidden md:flex flex-row justify-center gap-2 md:gap-4 mt-2 md:mt-4">
          {allImages.map((image, index) => {
            if (!image.showThumbnail) {
              return null
            }

            const thumbnailSrc =
              image.thumbnailUrl ||
              (image as { thumbnail_url?: string; thumbnail?: string }).thumbnail_url ||
              (image as { thumbnail?: string }).thumbnail ||
              image.url

            return (
              <button
                key={index}
                onClick={() => handleThumbnailClick(index)}
                className={`product-thumbnail basis-1/4 grow-0 aspect-[4/4] border border-2 border-background rounded-xl bg-orange overflow-hidden ${index === currentIndex ? 'outline outline-2 outline-orange' : ''
                  }`}>
                <div className="relative w-full h-full">
                  {image.backgroundUrl && (
                    <img
                      src={image.backgroundUrl}
                      alt={`${image.alt} background`}
                      className="absolute inset-0 w-full h-full min-w-full min-h-full object-cover"
                    />
                  )}
                  <img
                    src={thumbnailSrc}
                    alt={image.alt}
                    className={`absolute inset-0 w-full h-full object-cover ${image.invertForeground ? 'invert' : ''}`}
                  />
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ImageSlideshow
